import {
    ActionDescription,
    ActionKind,
    PropertyDescription,
    MutatingScreenKind,
    getPrimitiveNonHiddenColumns,
    InputOutputTables,
    SourceColumnKind,
    getSourceColumnSinglePath,
    getTableColumn,
    getTableName,
} from "../description";
import { BaseActionHandler, tokenForProperty } from "./base";
import {
    ComponentConfiguratorContext,
    ComponentBuilder,
    RequiredKind,
    WireInflationBackend,
    WireActionHydrator,
} from "../components";
import { ActionDescriptor } from "./action-descriptor";
import { makeNumberPropertyDescriptor } from "../components/descriptor-utils";
import { ColumnPropertyHandler, ColumnPropertyFlag } from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import { SerializedComponent } from "../../components/SerializedApp";
import { makeNumber, setColumnInRowForEditComponent } from "../builder-utils";
import { getFeatureSetting } from "../../feature-settings";
import { DescriptionToken } from "./action-handler";
import { LetBinding } from "../serialized-component";
import { withOutputContextForwardee } from "./set-columns";
import { getSourceColumnProperty } from "../components/property-utils";
import { asMaybeNumber } from "../../computation-model/data";

interface IncrementActionDescription extends ActionDescription {
    readonly kind: ActionKind.Increment;
    readonly sourceColumn: PropertyDescription;
    readonly amount: PropertyDescription;
}

const columnPropertyHandler = new ColumnPropertyHandler(
    "sourceColumn",
    "Column",
    [ColumnPropertyFlag.Editable, ColumnPropertyFlag.Required, ColumnPropertyFlag.EditedInApp],
    undefined,
    ["count", "sum"],
    getPrimitiveNonHiddenColumns,
    "number",
    PropertySection.Data
);

export class IncrementActionHandler extends BaseActionHandler<IncrementActionDescription> {
    public readonly kind = ActionKind.Increment;
    public readonly iconName = "08-01-calculator-app";

    public getDescriptor(
        _desc: IncrementActionDescription | undefined,
        _tables: InputOutputTables | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor | undefined {
        // if (mutatingScreenKind !== undefined) return undefined;

        return {
            name: "Increment Number",
            needsScreenContext: true,
            isLegacy: !getFeatureSetting("incrementAction"),
            properties: [
                columnPropertyHandler,
                makeNumberPropertyDescriptor(
                    "amount",
                    "Increment by",
                    "Enter a number",
                    RequiredKind.Required,
                    1,
                    mutatingScreenKind,
                    {
                        preferredNames: ["increment", "amount"],
                        isEditedInApp: false,
                    }
                ),
            ],
        };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: IncrementActionDescription,
        tables: InputOutputTables,
        containingScreenContext: SerializedComponent | undefined,
        outputContext: SerializedComponent,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;

        return withOutputContextForwardee(b, outputContext, (outputLets, outputForwardee) => {
            const propertyName = columnPropertyHandler.getColumnName(desc);
            if (propertyName === undefined) return undefined;

            if (tables === undefined || containingScreenContext === undefined) return undefined;

            const amount = makeNumber(builder, desc.amount, tables.input, false, containingScreenContext);
            if (amount === undefined) return undefined;

            const resultLet: LetBinding = [
                "result",
                b.clonePrimitive(b.add(b.getProperty(propertyName, outputForwardee, tables.output), amount)),
            ];

            const setColumnsResult = setColumnInRowForEditComponent(
                b,
                mutatingScreenKind,
                propertyName,
                b.getProperty(propertyName, outputForwardee, tables.output),
                undefined,
                tables.output,
                undefined,
                outputForwardee,
                { withDiode: false }
            );
            if (setColumnsResult === undefined) return undefined;
            const { lets, valueToModify, action, isEnabled } = setColumnsResult;

            const actions = [b.set(valueToModify, b.getVariable("result"))];
            if (action !== undefined) {
                actions.push(action);
            }
            let sequence = b.sequence(actions);
            if (isEnabled !== undefined) {
                sequence = b.if(isEnabled, sequence);
            }

            return b.holdAction(b.let([...outputLets, resultLet, ...lets], sequence));
        });
    }

    public getTokenizedDescription(
        desc: IncrementActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const amountToken = tokenForProperty(desc.amount, tables, ccc);
        const columntoken = tokenForProperty(desc.sourceColumn, tables, ccc);
        if (columntoken === undefined || amountToken === undefined) return undefined;

        return [
            columntoken,
            {
                kind: "string",
                value: " by ",
            },
            amountToken,
        ];
    }

    public inflate(ib: WireInflationBackend, desc: IncrementActionDescription): WireActionHydrator | undefined {
        const sourceColumn = getSourceColumnProperty(desc.sourceColumn);
        if (sourceColumn === undefined || sourceColumn.kind !== SourceColumnKind.DefaultContext) return undefined;

        const columnName = getSourceColumnSinglePath(sourceColumn);
        const destTable = ib.tables.output;
        const column = getTableColumn(destTable, columnName);
        if (column === undefined) return undefined;

        const [currentValueGetter] = ib.getValueGetterForProperty(desc.sourceColumn, false);
        const [amountGetter] = ib.getValueGetterForProperty(desc.amount, false);

        return vp => {
            // FIXME: I don't think this is a good idea.  We should be able to
            // distinguish the case of there being no output row, and the
            // output row being the same as the input row.
            const outputRow = vp.rowContext?.outputRow ?? vp.rowContext?.inputRow;
            if (outputRow === undefined) return undefined;

            const currentValue = asMaybeNumber(currentValueGetter(vp)) ?? 0;
            const amount = asMaybeNumber(amountGetter(vp)) ?? 1;
            const nextValue = currentValue + amount;

            return async ab => {
                const update = { [columnName]: nextValue };
                await ab.setColumnsInRow(getTableName(destTable), outputRow, update);
                return true;
            };
        };
    }
}
